// Empire Apps Chassis · elder · language
// Bilingual text helper for elder UIs.
// Primary in user's selected language, English secondary in smaller font below.

import type { BilingualText, LangCode } from "./types.ts";
import { DEFAULT_LANG, FALLBACK_LANG, SUPPORTED_LANGS } from "./types.ts";

export type I18nDictionary = Readonly<Record<string, Readonly<Record<LangCode, string>>>>;

const STORAGE_KEY = "empire.elder.lang";

export function detectSystemLang(): LangCode {
  if (typeof navigator === "undefined") return DEFAULT_LANG;
  const candidates: readonly string[] = navigator.languages?.length
    ? navigator.languages
    : [navigator.language ?? "en-IN"];
  for (const tag of candidates) {
    const head = tag.toLowerCase().split("-")[0] ?? "";
    if ((SUPPORTED_LANGS as readonly string[]).includes(head)) {
      return head as LangCode;
    }
  }
  return DEFAULT_LANG;
}

export function getSelectedLang(): LangCode {
  if (typeof localStorage === "undefined") return DEFAULT_LANG;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && (SUPPORTED_LANGS as readonly string[]).includes(stored)) {
    return stored as LangCode;
  }
  return detectSystemLang();
}

export function setSelectedLang(lang: LangCode): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(STORAGE_KEY, lang);
  if (typeof document !== "undefined") {
    document.documentElement.lang = lang;
    document.dispatchEvent(new CustomEvent("empire-lang-change", { detail: { lang } }));
  }
}

function pickFromDict(
  dict: I18nDictionary,
  key: string,
  lang: LangCode,
): string | null {
  const entry = dict[key];
  if (!entry) return null;
  const candidate = entry[lang];
  if (candidate) return candidate;
  return entry[FALLBACK_LANG] ?? null;
}

export function makeBilingual(
  dict: I18nDictionary,
  key: string,
  primaryLang: LangCode = getSelectedLang(),
): BilingualText {
  const primary = pickFromDict(dict, key, primaryLang) ?? `[${key}]`;
  const secondaryLang = primaryLang === FALLBACK_LANG ? DEFAULT_LANG : FALLBACK_LANG;
  const secondary = pickFromDict(dict, key, secondaryLang) ?? "";
  return {
    primary,
    secondary,
    primaryLang,
    secondaryLang,
  };
}

export function t(dict: I18nDictionary, key: string, lang?: LangCode): string {
  const target = lang ?? getSelectedLang();
  return pickFromDict(dict, key, target) ?? `[${key}]`;
}

export function isRTL(lang: LangCode): boolean {
  // None of our 6 supported langs is RTL today; reserved for future Urdu / Arabic-Sufi.
  return false;
}

export function languageDisplayName(lang: LangCode): string {
  const map: Record<LangCode, string> = {
    hi: "हिन्दी",
    ta: "தமிழ்",
    bn: "বাংলা",
    mr: "मराठी",
    gu: "ગુજરાતી",
    en: "English",
  };
  return map[lang];
}
